/**
 * Entry detail: a drawer on desktop, a full-height sheet on mobile.
 *
 * Shows who paid, what each person owes, the line items if a receipt was scanned, and
 * the actions the viewer is actually allowed to take.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Modal, useConfirm } from "../../components/ui/modal";
import { Spinner } from "../../components/ui/spinner";
import { api, type Member, type Transaction, type TransferLink } from "../../lib/api";
import { formatAmount, formatMoney, formatSigned, toneClass } from "../../lib/money";
import { cn } from "../../lib/utils";

interface Props {
  groupId: number;
  transactionId: number | null;
  members: Member[];
  currency: string;
  categoryNames: Map<number, string>;
  balancesRefetch: () => void;
  onClose: () => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

export function EntryDrawer({
  groupId,
  transactionId,
  members,
  currency,
  categoryNames,
  balancesRefetch,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: transaction, isPending } = useQuery({
    queryKey: ["transaction", groupId, transactionId],
    queryFn: () => api.get<Transaction>(`/groups/${groupId}/transactions/${transactionId}`),
    enabled: transactionId !== null,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/groups/${groupId}/transactions/${transactionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      balancesRefetch();
      onClose();
    },
  });

  const duplicate = useMutation({
    mutationFn: () =>
      api.post<Transaction>(`/groups/${groupId}/transactions/${transactionId}/duplicate`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      balancesRefetch();
      onClose();
    },
  });

  const restore = useMutation({
    mutationFn: () =>
      api.post<Transaction>(`/groups/${groupId}/transactions/${transactionId}/restore`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      void queryClient.invalidateQueries({ queryKey: ["transaction", groupId, transactionId] });
      balancesRefetch();
    },
  });

  // A transfer leg is half of a story; the other half lives in another group.
  const transferId = transaction?.transfer_id ?? null;
  const { data: link } = useQuery({
    queryKey: ["transfer-link", groupId, transferId],
    queryFn: () => api.get<TransferLink>(`/groups/${groupId}/transfers/${transferId}/link`),
    enabled: transferId !== null,
    retry: false,
  });

  const names = new Map(members.map((member) => [member.id, member.display_name]));
  const [askDelete, confirmDelete] = useConfirm();

  const subtitle = transaction
    ? [
        new Date(`${transaction.occurred_on}T00:00:00`).toLocaleDateString(undefined, {
          weekday: "short",
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        transaction.locked ? "locked" : null,
        transaction.deleted_at ? "deleted" : null,
        transaction.origin === "ai" ? "added via the assistant" : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <>
      {confirmDelete}
      <Modal
        open={transactionId !== null}
        onOpenChange={(open) => !open && onClose()}
        variant="drawer"
        title={transaction?.name ?? "Entry"}
        description={subtitle}
        footer={
          transaction?.deleted_at ? (
            <div className="flex w-full flex-wrap items-center gap-2">
              <p className="flex-1 text-xs text-muted">
                This entry is deleted. It is out of every balance until it comes back.
              </p>
              <Button
                variant="primary"
                disabled={restore.isPending || !transaction.can_edit}
                onClick={() => restore.mutate()}
              >
                {restore.isPending ? "Restoring…" : "Restore"}
              </Button>
            </div>
          ) : transaction ? (
            <div className="flex w-full flex-wrap items-center gap-2">
              {transaction.can_edit ? (
                <Button
                  onClick={() => navigate(`/g/${groupId}/t/${transaction.id}/edit`)}
                  variant="primary"
                >
                  Edit
                </Button>
              ) : (
                <p className="flex-1 text-xs text-muted">
                  {transaction.locked
                    ? "This entry is locked. Ask a group admin to change it."
                    : "Only the person who added this, or an admin, can change it."}
                </p>
              )}
              <Button onClick={() => duplicate.mutate()} disabled={duplicate.isPending}>
                Duplicate
              </Button>
              {transaction.can_edit ? (
                <Button
                  variant="danger"
                  className="ml-auto"
                  disabled={remove.isPending}
                  onClick={() =>
                    askDelete({
                      title: `Delete "${transaction.name}"?`,
                      body: "Balances update immediately. A group admin can restore it afterwards.",
                      confirmLabel: "Delete entry",
                      destructive: true,
                      onConfirm: () => remove.mutate(),
                    })
                  }
                >
                  Delete
                </Button>
              ) : null}
            </div>
          ) : null
        }
      >
        {isPending || !transaction ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <>
                <p className="num mb-3 text-3xl">
                  {formatMoney(transaction.total_base_cents, currency)}
                </p>

                <div className="divide-y divide-rule">
                  {transaction.currency_code !== currency ? (
                    <Row label="Original">
                      <span className="num">
                        {formatMoney(transaction.total_original_cents, transaction.currency_code)}
                      </span>{" "}
                      <span className="text-xs text-muted">
                        at {transaction.fx_rate_to_base}
                      </span>
                    </Row>
                  ) : null}
                  {transaction.payer_member_id ? (
                    <Row label="Paid by">{names.get(transaction.payer_member_id)}</Row>
                  ) : null}
                  {transaction.from_member_id && transaction.to_member_id ? (
                    <Row label="Payment">
                      {names.get(transaction.from_member_id)} → {names.get(transaction.to_member_id)}
                    </Row>
                  ) : null}
                  {transaction.split ? <Row label="Split">{transaction.split}</Row> : null}
                  {transaction.category_id ? (
                    <Row label="Category">{categoryNames.get(transaction.category_id)}</Row>
                  ) : null}
                  {transaction.comment ? <Row label="Comment">{transaction.comment}</Row> : null}
                </div>

                {link ? (
                  <div className="mt-4 rounded-lg border border-rule bg-sunken px-3 py-2.5 text-sm">
                    <p className="text-xs text-muted">
                      {link.direction === "source"
                        ? "These balances came from another group."
                        : "These balances moved to another group."}
                    </p>
                    {link.can_view ? (
                      <button
                        type="button"
                        className="mt-1 text-accent underline underline-offset-2"
                        onClick={() => {
                          onClose();
                          navigate(`/g/${link.group_id}`);
                        }}
                      >
                        Open {link.group_name}
                        {link.archived ? " (archived)" : ""} →
                      </button>
                    ) : (
                      <p className="mt-1 text-xs text-muted">
                        You are not a member of that group, so it cannot be opened from here.
                      </p>
                    )}
                  </div>
                ) : null}

                {transaction.line_items.length > 0 ? (
                  <section className="mt-5">
                    <h3 className="mb-1 text-2xs uppercase tracking-widest text-muted">
                      Line items
                    </h3>
                    <ul className="divide-y divide-rule text-sm">
                      {transaction.line_items.map((item) => (
                        <li key={item.id} className="flex justify-between gap-3 py-1.5">
                          <span className="min-w-0">
                            <span className="block truncate">{item.name}</span>
                            <span className="text-xs text-muted">
                              {item.assignee_member_ids.length
                                ? item.assignee_member_ids
                                    .map((id) => names.get(id) ?? "?")
                                    .join(", ")
                                : "Shared"}
                            </span>
                          </span>
                          <span className="num">
                            {formatAmount(item.amount_original_cents, transaction.currency_code)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <section className="mt-5">
                  <h3 className="mb-1 text-2xs uppercase tracking-widest text-muted">
                    Balance effect
                  </h3>
                  <ul className="divide-y divide-rule text-sm">
                    {Object.entries(transaction.ledger)
                      .map(([memberId, delta]) => [Number(memberId), delta] as const)
                      .sort((a, b) => b[1] - a[1])
                      .map(([memberId, delta]) => (
                        <li key={memberId} className="flex justify-between gap-3 py-1.5">
                          <span>{names.get(memberId) ?? `Member ${memberId}`}</span>
                          <span className={cn("num", toneClass(delta))}>
                            {formatSigned(delta, currency)}
                          </span>
                        </li>
                      ))}
                  </ul>
                </section>
          </>
        )}
      </Modal>
    </>
  );
}
